// Linking scanned documents (scanned_documents + bucket "scanned-docs") to the
// place where each entity's documents live. Mirrors car-docs-inbox/app/pipeline.py
// so a manual link looks exactly like an automatic one:
//   car        -> car-docs/{car_id}/{name}                    (Documentos on the car page)
//   customer   -> client-docs + client_documents slot          (ClientDocuments on the profile)
//   company    -> client-docs + client_documents slot
//   validacion -> validacion-docs + validacion_files row
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import supabase from "../../supabase-client";

// Same CDN worker setup as utility/comprobantes.js.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export const DOC_TYPE_LABELS = {
  tarjeta_circulacion: "Tarjeta de circulación",
  constancia_registro: "Constancia de registro vehicular",
  recibo_icvnl: "Recibo ICVNL",
  poliza_seguro: "Póliza de seguro",
  factura_vehiculo: "Factura del vehículo",
  verificacion: "Verificación",
  otro_vehiculo: "Documento del vehículo",
  ine_front: "INE Frente",
  ine_back: "INE Reverso",
  license_front: "Licencia Frente",
  license_back: "Licencia Reverso",
  comprobante_domicilio: "Comprobante de domicilio",
  csf: "Constancia de Situación Fiscal",
  acta_constitutiva: "Acta Constitutiva",
  otro: "Otro documento",
};

export const ENTITY_LABELS = {
  car: "Auto",
  customer: "Cliente",
  company: "Empresa",
  validacion: "Validación",
};

export const MATCH_LABELS = {
  vin: "VIN",
  placa: "Placa",
  curp: "CURP",
  rfc: "RFC",
  mismo_correo: "Mismo correo",
  manual: "Manual",
};

// Slots each screen offers (same keys as ClientDocuments / NewValidacion).
export const SLOTS = {
  customer: [
    { value: "ine_front", label: "INE Frente" },
    { value: "ine_back", label: "INE Reverso" },
    { value: "license_front", label: "Licencia Frente" },
    { value: "license_back", label: "Licencia Reverso" },
    { value: "csf", label: "Constancia de Situación Fiscal" },
    { value: "proof_address", label: "Comprobante de domicilio" },
    { value: "other", label: "Otros documentos" },
  ],
  company: [
    { value: "acta_constitutiva", label: "Acta Constitutiva" },
    { value: "csf", label: "Constancia de Situación Fiscal" },
    { value: "proof_address", label: "Comprobante de domicilio" },
    { value: "ine_rep_front", label: "INE Rep. Legal Frente" },
    { value: "ine_rep_back", label: "INE Rep. Legal Reverso" },
    { value: "other", label: "Otros documentos" },
  ],
  validacion: [
    { value: "ine_front", label: "INE Frente" },
    { value: "ine_back", label: "INE Reverso" },
    { value: "license_front", label: "Licencia Frente" },
    { value: "license_back", label: "Licencia Reverso" },
    { value: "other", label: "Otro archivo" },
  ],
};

const DEFAULT_SLOT = {
  customer: { comprobante_domicilio: "proof_address" },
  company: { comprobante_domicilio: "proof_address", ine_front: "ine_rep_front", ine_back: "ine_rep_back" },
  validacion: {},
};

export function defaultSlot(entityType, docType) {
  const slots = SLOTS[entityType] || [];
  const mapped = DEFAULT_SLOT[entityType]?.[docType] || docType;
  if (slots.some((s) => s.value === mapped)) return mapped;
  return entityType === "validacion" ? "other" : slots[0]?.value;
}

/** Category the document type belongs to, to pre-select the entity kind. */
export function defaultEntityType(doc) {
  if (doc.category === "vehiculo") return "car";
  if (doc.category === "empresa") return "company";
  return "customer";
}

const ext = (path) => (path || "").split(".").pop().toLowerCase();

const safeName = (name) =>
  name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_");

function carFileName(doc, plate) {
  const x = doc.extracted || {};
  const p = (x.placa || plate || "").toUpperCase();
  const names = {
    tarjeta_circulacion: `Tarjeta de Circulacion ${p}`.trim(),
    constancia_registro: "Constancia de Registro Vehicular",
    recibo_icvnl: x.fecha ? `Recibo ICVNL ${x.fecha}` : "Recibo ICVNL",
    poliza_seguro: x.folio ? `Poliza de Seguro ${x.folio}` : "Poliza de Seguro",
    factura_vehiculo: "Factura del Vehiculo",
    verificacion: x.fecha ? `Verificacion ${x.fecha}` : "Verificacion",
  };
  const base = names[doc.doc_type] || (doc.summary || "Documento").replace(/[\\/:*?"<>|]/g, "").slice(0, 80);
  return `${base}.${ext(doc.file_path)}`;
}

function personFileName(doc) {
  const x = doc.extracted || {};
  let base = DOC_TYPE_LABELS[doc.doc_type] || "Documento";
  if (doc.doc_type === "comprobante_domicilio" && x.fecha) base += ` ${x.fecha}`;
  return `${base}.${ext(doc.file_path)}`;
}

export async function signedUrl(doc) {
  const { data } = await supabase.storage.from("scanned-docs").createSignedUrl(doc.file_path, 3600);
  return data?.signedUrl || null;
}

async function upload(bucket, path, blob, contentType) {
  const { error } = await supabase.storage.from(bucket).upload(path, blob, { contentType, upsert: false });
  return error;
}

/**
 * Link (or re-link) a scanned document. `target` = { entityType, entityId, label, plate?, slot? }.
 * With `keepMatch`, the row keeps how it was originally matched (used when an edited
 * file replaces the linked copy).
 */
export async function linkDocument(doc, target, userId, { keepMatch = false } = {}) {
  if (doc.status === "linked") await unlinkDocument(doc, userId);

  const { data: blob, error: dlErr } = await supabase.storage.from("scanned-docs").download(doc.file_path);
  if (dlErr) throw dlErr;
  const contentType = doc.mime_type || blob.type;
  let fields;

  if (target.entityType === "car") {
    // Re-linking an edited file keeps the name the car already shows (extension may change).
    const name =
      keepMatch && doc.target_filename
        ? `${doc.target_filename.replace(/\.[^.]+$/, "")}.${ext(doc.file_path)}`
        : carFileName(doc, target.plate);
    let path = `${target.entityId}/${name}`;
    let err = await upload("car-docs", path, blob, contentType);
    if (err) {
      // Name taken by another file: keep both, like the inbox service does.
      const stem = name.replace(/\.[^.]+$/, "");
      path = `${target.entityId}/${stem} (${new Date().toISOString().slice(0, 16).replace("T", " ").replace(":", "")}).${ext(name)}`;
      err = await upload("car-docs", path, blob, contentType);
      if (err) throw err;
    }
    fields = { target_bucket: "car-docs", target_path: path, target_filename: path.split("/").slice(1).join("/"), target_file_type: null };
  } else if (target.entityType === "validacion") {
    const name = personFileName(doc);
    const path = `${target.entityId}/${target.slot}_${Date.now()}_${safeName(name)}`;
    const err = await upload("validacion-docs", path, blob, contentType);
    if (err) throw err;
    const { error } = await supabase.from("validacion_files").insert({
      validacion_id: target.entityId,
      file_type: target.slot,
      file_url: path,
      file_name: name,
    });
    if (error) throw error;
    await supabase.from("validacion_activity").insert({
      validacion_id: target.entityId,
      user_id: userId,
      action: "file_added",
      content: `Vinculó documento escaneado: ${DOC_TYPE_LABELS[doc.doc_type] || name}`,
      metadata: { file_url: path, file_name: name, file_type: target.slot, scanned_document_id: doc.id },
    });
    fields = { target_bucket: "validacion-docs", target_path: path, target_file_type: target.slot, target_filename: null };
  } else {
    const name = personFileName(doc);
    const path = `${target.entityType}/${target.entityId}/${target.slot}_${Date.now()}_${safeName(name)}`;
    const err = await upload("client-docs", path, blob, contentType);
    if (err) throw err;
    // Anything already in the slot is archived to "Otros documentos" by a DB trigger.
    const { error } =
      target.slot === "other"
        ? await supabase.from("client_other_documents").insert({
            entity_type: target.entityType,
            entity_id: Number(target.entityId),
            file_url: path,
            file_name: name,
            label: DOC_TYPE_LABELS[doc.doc_type] || null,
            created_by: userId || null,
          })
        : await supabase.from("client_documents").upsert(
          {
            entity_type: target.entityType,
            entity_id: Number(target.entityId),
            file_type: target.slot,
            file_url: path,
            file_name: name,
            created_by: userId || null,
          },
          { onConflict: "entity_type,entity_id,file_type" }
        );
    if (error) throw error;
    fields = { target_bucket: "client-docs", target_path: path, target_file_type: target.slot, target_filename: null };
  }

  const { error } = await supabase
    .from("scanned_documents")
    .update({
      status: "linked",
      entity_type: target.entityType,
      entity_id: String(target.entityId),
      ...fields,
      ...(keepMatch
        ? { match_method: doc.match_method, linked_by: doc.linked_by, linked_at: doc.linked_at }
        : { match_method: "manual", match_note: null, linked_by: userId || null, linked_at: new Date().toISOString() }),
    })
    .eq("id", doc.id);
  if (error) throw error;
}

// ---------------------------------------------------------------- rotate / crop

/** The document as a drawable image. PDFs are rendered (first page) at print resolution. */
export async function loadEditableImage(doc) {
  const { data: blob, error } = await supabase.storage.from("scanned-docs").download(doc.file_path);
  if (error) throw error;
  if ((doc.mime_type || blob.type || "").includes("pdf") || doc.file_path.endsWith(".pdf")) {
    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 200 / 72 });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    return { source: canvas, width: canvas.width, height: canvas.height, wasPdf: true };
  }
  const bitmap = await createImageBitmap(blob);
  return { source: bitmap, width: bitmap.width, height: bitmap.height, wasPdf: false };
}

/**
 * Draw `image` turned `rotation` degrees clockwise, then cut `crop`
 * ({x, y, w, h} as fractions of the turned image; null = whole image).
 */
export function renderEdit(image, rotation, crop) {
  const quarter = rotation % 180 !== 0;
  const tw = quarter ? image.height : image.width;
  const th = quarter ? image.width : image.height;
  const turned = document.createElement("canvas");
  turned.width = tw;
  turned.height = th;
  const ctx = turned.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, tw, th);
  ctx.translate(tw / 2, th / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(image.source, -image.width / 2, -image.height / 2);
  if (!crop) return turned;
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(crop.w * tw));
  out.height = Math.max(1, Math.round(crop.h * th));
  out.getContext("2d").drawImage(turned, crop.x * tw, crop.y * th, crop.w * tw, crop.h * th, 0, 0, out.width, out.height);
  return out;
}

async function sha256Hex(blob) {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function replaceFile(doc, path, mimeType, sha, userId, extra = {}) {
  const { data: updated, error } = await supabase
    .from("scanned_documents")
    .update({ file_path: path, mime_type: mimeType, sha256: sha, ...extra })
    .eq("id", doc.id)
    .select("*")
    .single();
  if (error) throw error;
  // A linked document: put the corrected file where the car / person sees it.
  if (doc.status === "linked") {
    await linkDocument(
      updated,
      { entityType: doc.entity_type, entityId: doc.entity_id, slot: doc.target_file_type },
      userId,
      { keepMatch: true }
    );
  }
}

/** Save a rotated / cropped version. The first edit remembers the original file. */
export async function saveEdit(doc, image, rotation, crop, userId) {
  const canvas = renderEdit(image, rotation, crop);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  const now = new Date();
  const path = `edits/${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, "0")}/${crypto.randomUUID()}.jpg`;
  const { error } = await supabase.storage.from("scanned-docs").upload(path, blob, { contentType: "image/jpeg" });
  if (error) throw error;
  await replaceFile(doc, path, "image/jpeg", await sha256Hex(blob), userId, {
    original_file_path: doc.original_file_path || doc.file_path,
  });
}

/** Undo every edit: point back at the file as it arrived. */
export async function restoreOriginal(doc, userId) {
  if (!doc.original_file_path) return;
  const isPdf = doc.original_file_path.endsWith(".pdf");
  await replaceFile(doc, doc.original_file_path, isPdf ? "application/pdf" : "image/jpeg", null, userId, {
    original_file_path: null,
  });
}

/** Remove the linked copy; the scan itself stays in scanned-docs. */
export async function unlinkDocument(doc, userId, nextStatus = "unlinked") {
  if (doc.status === "linked" && doc.target_path) {
    if (doc.target_bucket === "client-docs") {
      await unlinkClientDoc(doc);
    } else if (doc.target_bucket === "validacion-docs") {
      await supabase.from("validacion_files").delete().eq("validacion_id", doc.entity_id).eq("file_url", doc.target_path);
      await supabase.from("validacion_activity").insert({
        validacion_id: doc.entity_id,
        user_id: userId,
        action: "file_removed",
        content: `Desvinculó documento escaneado: ${DOC_TYPE_LABELS[doc.doc_type] || doc.target_path}`,
        metadata: { file_url: doc.target_path, scanned_document_id: doc.id },
      });
    }
    const { error: rmErr } = await supabase.storage.from(doc.target_bucket).remove([doc.target_path]);
    if (rmErr) throw rmErr;
  }
  const { error } = await supabase
    .from("scanned_documents")
    .update({
      status: nextStatus,
      entity_type: null,
      entity_id: null,
      target_bucket: null,
      target_path: null,
      target_file_type: null,
      target_filename: null,
      match_method: null,
      linked_by: null,
      linked_at: null,
    })
    .eq("id", doc.id);
  if (error) throw error;
}

// Take this document's copy out of the client. If it was in a slot, the file
// that was there before it (archived in "Otros documentos") goes back, so a
// wrong link never costs the client their previous document.
async function unlinkClientDoc(doc) {
  const entityId = Number(doc.entity_id);
  const { data: removed, error } = await supabase
    .from("client_documents")
    .delete()
    .eq("entity_type", doc.entity_type)
    .eq("entity_id", entityId)
    .eq("file_url", doc.target_path)
    .select("file_type");
  if (error) throw error;
  // The delete trigger archived it (or it already was in Otros): drop that too.
  const { error: otherErr } = await supabase.from("client_other_documents").delete().eq("file_url", doc.target_path);
  if (otherErr) throw otherErr;

  const slot = removed?.[0]?.file_type;
  if (!slot) return;
  const { data: prev } = await supabase
    .from("client_other_documents")
    .select("*")
    .eq("entity_type", doc.entity_type)
    .eq("entity_id", entityId)
    .eq("original_file_type", slot)
    .order("replaced_at", { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();
  if (!prev) return;
  const { error: restoreErr } = await supabase.from("client_documents").insert({
    entity_type: doc.entity_type,
    entity_id: entityId,
    file_type: slot,
    file_url: prev.file_url,
    file_name: prev.file_name,
    created_by: prev.created_by,
  });
  if (restoreErr) throw restoreErr;
  await supabase.from("client_other_documents").delete().eq("id", prev.id);
}

/** Delete a document for good: its linked copy, its scan (and pre-edit original), and the row. */
export async function deleteDocument(doc, userId) {
  if (doc.status === "linked") await unlinkDocument(doc, userId);
  const files = [doc.file_path, doc.original_file_path].filter(Boolean);
  const { error: rmErr } = await supabase.storage.from("scanned-docs").remove(files);
  if (rmErr) throw rmErr;
  const { error } = await supabase.from("scanned_documents").delete().eq("id", doc.id);
  if (error) throw error;
}

/** Search candidates to link to. Returns [{ id, label, sublabel, plate? }]. */
export async function searchEntities(entityType, term) {
  const t = (term || "").trim();
  if (!t) return [];
  const like = `%${t}%`;
  if (entityType === "car") {
    const { data } = await supabase
      .from("cars")
      .select("id, plate, vin, model(name, brand(name))")
      .or(`plate.ilike.${like},vin.ilike.${like}`)
      .limit(15);
    return (data || []).map((c) => ({
      id: String(c.id),
      label: c.plate || c.vin,
      sublabel: [c.vin, c.model?.brand?.name, c.model?.name].filter(Boolean).join(" · "),
      plate: c.plate,
    }));
  }
  if (entityType === "customer") {
    const { data } = await supabase.rpc("search_customers", { p_term: t, p_limit: 15, p_offset: 0 });
    return (data || []).map((c) => ({
      id: String(c.id),
      label: [c.first_name, c.last_name].filter(Boolean).join(" "),
      sublabel: [c.email, c.phone].filter(Boolean).join(" · "),
    }));
  }
  if (entityType === "company") {
    const { data } = await supabase.from("companies").select("id, name, rfc").or(`name.ilike.${like},rfc.ilike.${like}`).limit(15);
    return (data || []).map((c) => ({ id: String(c.id), label: c.name, sublabel: c.rfc }));
  }
  const { data } = await supabase
    .from("validaciones")
    .select("id, first_names, father_last_name, mother_last_name, curp, status, created_at")
    .or(`curp.ilike.${like},first_names.ilike.${like},father_last_name.ilike.${like},mother_last_name.ilike.${like}`)
    .order("created_at", { ascending: false })
    .limit(15);
  return (data || []).map((v) => ({
    id: v.id,
    label: [v.first_names, v.father_last_name, v.mother_last_name].filter(Boolean).join(" "),
    sublabel: [v.curp, v.status].filter(Boolean).join(" · "),
  }));
}

/** Human label for whatever a row is linked to (or suggested for). */
export async function entityLabels(rows) {
  const ids = { car: new Set(), customer: new Set(), company: new Set(), validacion: new Set() };
  for (const r of rows) {
    if (r.entity_type && r.entity_id) ids[r.entity_type].add(r.entity_id);
  }
  const out = {};
  const put = (type, id, label) => (out[`${type}:${id}`] = label);
  const q = [];
  if (ids.car.size)
    q.push(supabase.from("cars").select("id, plate, vin").in("id", [...ids.car]).then(({ data }) =>
      (data || []).forEach((c) => put("car", String(c.id), c.plate || c.vin))));
  if (ids.customer.size)
    q.push(supabase.from("customers").select("id, first_name, last_name").in("id", [...ids.customer]).then(({ data }) =>
      (data || []).forEach((c) => put("customer", String(c.id), [c.first_name, c.last_name].filter(Boolean).join(" ")))));
  if (ids.company.size)
    q.push(supabase.from("companies").select("id, name").in("id", [...ids.company]).then(({ data }) =>
      (data || []).forEach((c) => put("company", String(c.id), c.name))));
  if (ids.validacion.size)
    q.push(supabase.from("validaciones").select("id, first_names, father_last_name").in("id", [...ids.validacion]).then(({ data }) =>
      (data || []).forEach((v) => put("validacion", v.id, [v.first_names, v.father_last_name].filter(Boolean).join(" ")))));
  await Promise.all(q);
  return out;
}

export function entityRoute(type, id) {
  if (type === "car") return `/cars/${id}`;
  if (type === "customer") return `/customers/edit?id=${id}`;
  if (type === "company") return `/companies/edit?id=${id}`;
  if (type === "validacion") return `/validaciones/${id}`;
  return null;
}

// The name read from a document vs. the customer it's linked to. Tolerates
// order (INE prints surnames first), accents/Ñ, and truncated words (INE back
// cuts names short), so only a real different person comes out as a mismatch.
function nameTokens(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z ]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1);
}

export function nameMismatch(doc, linkedName) {
  if (doc.status !== "linked" || doc.entity_type !== "customer") return false;
  const read = nameTokens(doc.extracted?.nombre);
  const known = nameTokens(linkedName);
  if (!read.length || !known.length) return false;
  const same = (a, b) => a === b || (Math.min(a.length, b.length) >= 3 && (a.startsWith(b) || b.startsWith(a)));
  const hits = known.filter((k) => read.some((r) => same(r, k))).length;
  return hits < Math.min(2, known.length);
}
